"use client";

import React, { useEffect, useRef, useState } from "react";
import { Star } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const testimonials = [{
  id: 1,
  name: "Paivm",
  location: "Google review · Sep 2026",
  text: "I'm very thankful to Dr Zanos. I came as I did not feel my leg from knee to foot. After 3 sessions I started to feel my leg and to feel good myself. I'm also a manual therapist and I can really feel the professionalism that Dr Zanos and his clinic provide.",
  rating: 5
}, {
  id: 2,
  name: "Pablo M.",
  location: "Google review · Sep 2026",
  text: "I work out and hike/run quite a lot. Have received amazing care at Bodywerks. It has helped me a lot keeping my body aligned and out of trouble. Highly recommended!",
  rating: 5
}, {
  id: 3,
  name: "Thaer J.",
  location: "Google review · Aug 2024",
  text: "I've been having problems with sciatica to the point where I was not able to walk. 2 sessions later I saw an improvement I didn't think was possible. They walk you through every step of the treatment!",
  rating: 5
}];
export default function TestimonialsFromLimassolPatients() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="testimonials-from-limassol-patients" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div ref={ref} className={`flex flex-col gap-12 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground tracking-tight">
              Patient Stories
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Hear directly from those who have experienced our dedicated care and transformative treatments.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, i) => <div data-index={i} key={testimonial.id} className="h-full">
                <Card className="h-full bg-[#FFFFFF] border-none shadow-[0_4px_12px_rgba(0,0,0,0.08)] flex flex-col transition-transform duration-300 hover:-translate-y-1">
                  <CardContent className="pt-8 px-8 pb-8 flex flex-col flex-grow gap-6">
                    <div className="flex gap-1">
                      {Array.from({
                    length: testimonial.rating
                  }).map((_, starIndex) => <Star key={starIndex} className="size-5 fill-[#3B8FD4] text-[#3B8FD4]" />)}
                    </div>
                    
                    <blockquote className="flex-grow">
                      <p className="text-foreground/80 leading-relaxed text-base">
                        "{testimonial.text}"
                      </p>
                    </blockquote>
                    
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-border/40">
                      <span className="font-sans font-bold text-foreground">
                        {testimonial.name}
                      </span>
                      <Badge variant="secondary" className="bg-[#E2E8F0] text-slate-700 hover:bg-[#E2E8F0]/80 border-none font-medium">
                        {testimonial.location}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}